import { modelUrl } from "@/lib/ai/detection/model-url"
import * as tf from "./tf"
import { FaceDetectionResult, Rect } from "./face-detection"
import { clamp } from "@/utils/math"
import { LazyGraphModel } from "./lazy-graph-model"
import PQueue from "p-queue"

export const UNIFACE_FACE_PARSER_INPUT_SIZE = 512
export const UNIFACE_FACE_PARSER_MODEL_PATH = modelUrl("uniface-face-parser-tfjs-int8")

export type FaceParsingPart = "entire" | "facialSkin" | "eyebrows" | "eyes" | "lips"

export interface FaceParsingResult {
	classMask: Uint8Array
	width: number
	height: number
	crop: Rect
}

const FACE_PARSING_CLASS_IDS = {
	background: 0,
	skin: 1,
	leftBrow: 2,
	rightBrow: 3,
	leftEye: 4,
	rightEye: 5,
	eyeGlass: 6,
	nose: 10,
	mouth: 11,
	upperLip: 12,
	lowerLip: 13,
} as const

const FACE_PARSING_PART_CLASSES: Record<Exclude<FaceParsingPart, "entire">, number[]> = {
	facialSkin: [FACE_PARSING_CLASS_IDS.skin, FACE_PARSING_CLASS_IDS.nose],
	eyebrows: [FACE_PARSING_CLASS_IDS.leftBrow, FACE_PARSING_CLASS_IDS.rightBrow],
	eyes: [
		FACE_PARSING_CLASS_IDS.leftEye,
		FACE_PARSING_CLASS_IDS.rightEye,
		FACE_PARSING_CLASS_IDS.eyeGlass,
	],
	lips: [
		FACE_PARSING_CLASS_IDS.mouth,
		FACE_PARSING_CLASS_IDS.upperLip,
		FACE_PARSING_CLASS_IDS.lowerLip,
	],
}

export class UniFaceFaceParser {
	private model = new LazyGraphModel(UNIFACE_FACE_PARSER_MODEL_PATH)
	private inferenceQueue = new PQueue({ concurrency: 1 })

	public async loadModel(onProgress?: (progress: number) => void) {
		await this.model.load(onProgress)
	}

	public cleanup() {
		this.model.cleanup()
	}

	public process(
		input: ImageBitmap,
		faceRect: Rect,
		confidenceThreshold = 0,
	): Promise<FaceParsingResult> {
		return this.inferenceQueue.add(() =>
			this.processInternal(input, faceRect, confidenceThreshold),
		)
	}

	private async processInternal(
		input: ImageBitmap,
		faceRect: Rect,
		confidenceThreshold: number,
	): Promise<FaceParsingResult> {
		const model = await this.model.load()

		const crop = createFaceParserCrop(faceRect, input.width, input.height)
		const pixelCrop = toPixelCrop(crop, input.width, input.height)
		const imageData = cropImageBitmapToImageData(input, pixelCrop)
		let inputTensor: tf.Tensor | undefined
		let outputs: tf.Tensor[] = []
		let classMaskTensor: tf.Tensor | undefined

		try {
			inputTensor = tf.tidy(() => {
				const pixels = tf.browser.fromPixels(imageData)
				const resized = tf.image.resizeBilinear(pixels, [
					UNIFACE_FACE_PARSER_INPUT_SIZE,
					UNIFACE_FACE_PARSER_INPUT_SIZE,
				])
				const rgb = tf.div(tf.cast(resized, "float32"), 255)
				const mean = tf.tensor1d([0.485, 0.456, 0.406])
				const std = tf.tensor1d([0.229, 0.224, 0.225])
				return tf.expandDims(tf.div(tf.sub(rgb, mean), std), 0)
			})

			const rawOutputs = model.execute({ input: inputTensor })
			outputs = Array.isArray(rawOutputs) ? rawOutputs : [rawOutputs]
			const logits = selectFaceParserLogits(outputs)
			classMaskTensor = tf.tidy(() => createClassMaskTensor(logits, confidenceThreshold))
			const modelClassMask = new Uint8Array(await classMaskTensor.data())
			const classMask = resampleClassMaskToCrop({
				classMask: modelClassMask,
				sourceWidth: UNIFACE_FACE_PARSER_INPUT_SIZE,
				sourceHeight: UNIFACE_FACE_PARSER_INPUT_SIZE,
				cropWidth: pixelCrop.width,
				cropHeight: pixelCrop.height,
			})

			return {
				classMask,
				width: pixelCrop.width,
				height: pixelCrop.height,
				crop,
			}
		} finally {
			classMaskTensor?.dispose()
			outputs.forEach((tensor) => tensor.dispose())
			inputTensor?.dispose()
		}
	}
}

export function createFaceParserCrop(
	faceRect: Rect,
	imageWidth: number,
	imageHeight: number,
): Rect {
	const faceX = faceRect.x * imageWidth
	const faceY = faceRect.y * imageHeight
	const faceWidth = faceRect.width * imageWidth
	const faceHeight = faceRect.height * imageHeight
	const centerX = faceX + faceWidth / 2
	const centerY = faceY + faceHeight / 2

	const cropWidth = Math.min(faceWidth, imageWidth)
	const cropHeight = Math.min(faceHeight, imageHeight)
	const cropX = clamp(centerX - cropWidth / 2, 0, Math.max(0, imageWidth - cropWidth))
	const cropY = clamp(centerY - cropHeight / 2, 0, Math.max(0, imageHeight - cropHeight))

	return {
		x: cropX / imageWidth,
		y: cropY / imageHeight,
		width: cropWidth / imageWidth,
		height: cropHeight / imageHeight,
	}
}

export function getFaceParsingSourceRect(
	face: Pick<FaceDetectionResult, "faceRect" | "unPaddedRect">,
) {
	return face.unPaddedRect ?? face.faceRect
}

export function createFaceParsingPartMask(
	classMask: Uint8Array,
	part: Exclude<FaceParsingPart, "entire">,
) {
	const classIds = new Set(FACE_PARSING_PART_CLASSES[part])
	const mask = new Float32Array(classMask.length)
	for (let index = 0; index < classMask.length; index += 1) {
		mask[index] = classIds.has(classMask[index] ?? FACE_PARSING_CLASS_IDS.background) ? 1 : 0
	}
	return mask
}

export function projectFaceParsingPartMask(options: {
	partMask: Float32Array
	partWidth: number
	partHeight: number
	crop: Rect
	outputWidth: number
	outputHeight: number
}) {
	const result = new Float32Array(options.outputWidth * options.outputHeight)
	const startX = Math.max(0, Math.floor(options.crop.x * options.outputWidth))
	const startY = Math.max(0, Math.floor(options.crop.y * options.outputHeight))
	const endX = Math.min(
		options.outputWidth,
		Math.ceil((options.crop.x + options.crop.width) * options.outputWidth),
	)
	const endY = Math.min(
		options.outputHeight,
		Math.ceil((options.crop.y + options.crop.height) * options.outputHeight),
	)

	for (let y = startY; y < endY; y += 1) {
		const sourceY = clamp(
			Math.floor(
				(((y + 0.5) / options.outputHeight - options.crop.y) / options.crop.height) *
					options.partHeight,
			),
			0,
			options.partHeight - 1,
		)
		for (let x = startX; x < endX; x += 1) {
			const outputIndex = y * options.outputWidth + x
			const sourceX = clamp(
				Math.floor(
					(((x + 0.5) / options.outputWidth - options.crop.x) / options.crop.width) *
						options.partWidth,
				),
				0,
				options.partWidth - 1,
			)
			const value = options.partMask[sourceY * options.partWidth + sourceX] ?? 0
			result[outputIndex] = value
		}
	}

	return result
}

function toPixelCrop(crop: Rect, imageWidth: number, imageHeight: number) {
	const x = Math.round(crop.x * imageWidth)
	const y = Math.round(crop.y * imageHeight)
	const width = Math.max(1, Math.round(crop.width * imageWidth))
	const height = Math.max(1, Math.round(crop.height * imageHeight))

	return {
		x: clamp(x, 0, imageWidth - 1),
		y: clamp(y, 0, imageHeight - 1),
		width: Math.min(width, imageWidth - x),
		height: Math.min(height, imageHeight - y),
	}
}

function cropImageBitmapToImageData(
	input: ImageBitmap,
	crop: { x: number; y: number; width: number; height: number },
) {
	const canvas = new OffscreenCanvas(crop.width, crop.height)
	const context = canvas.getContext("2d")
	if (!context) {
		throw new Error("Could not create face parser crop context")
	}

	context.drawImage(input, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height)
	return context.getImageData(0, 0, crop.width, crop.height)
}

function resampleClassMaskToCrop(options: {
	classMask: Uint8Array
	sourceWidth: number
	sourceHeight: number
	cropWidth: number
	cropHeight: number
}) {
	const result = new Uint8Array(options.cropWidth * options.cropHeight)
	for (let y = 0; y < options.cropHeight; y += 1) {
		const sourceY = clamp(
			Math.floor(((y + 0.5) / options.cropHeight) * options.sourceHeight),
			0,
			options.sourceHeight - 1,
		)
		for (let x = 0; x < options.cropWidth; x += 1) {
			const sourceX = clamp(
				Math.floor(((x + 0.5) / options.cropWidth) * options.sourceWidth),
				0,
				options.sourceWidth - 1,
			)
			result[y * options.cropWidth + x] =
				options.classMask[sourceY * options.sourceWidth + sourceX] ?? 0
		}
	}
	return result
}

export function selectFaceParserLogits(outputs: tf.Tensor[]) {
	const byName = new Map<string, tf.Tensor>()
	outputs.forEach((tensor) => {
		const rawName: unknown = Reflect.get(tensor, "name")
		const name = typeof rawName === "string" ? rawName : undefined
		if (!name) {
			return
		}
		byName.set(name, tensor)
		byName.set(name.replace(/:0$/, ""), tensor)
	})

	const named = byName.get("Identity") ?? byName.get("Identity:0")
	if (named && isFaceParserLogits(named)) {
		return named
	}

	const logits = outputs.find(isFaceParserLogits)
	if (!logits) {
		throw new Error(
			`UniFace face parser output logits were not found: ${outputs
				.map((tensor) => `${getTensorName(tensor)}:${tensor.shape.join("x")}`)
				.join(", ")}`,
		)
	}
	return logits
}

function createClassMaskTensor(logits: tf.Tensor, confidenceThreshold: number) {
	const classMask = tf.squeeze(tf.argMax(logits, 3), [0])
	if (confidenceThreshold <= 0) {
		return classMask
	}

	const confidence = tf.squeeze(tf.max(tf.softmax(logits, 3), 3), [0])
	const confident = tf.greaterEqual(confidence, confidenceThreshold)
	return tf.where(confident, classMask, tf.zerosLike(classMask))
}

function getTensorName(tensor: tf.Tensor) {
	const rawName: unknown = Reflect.get(tensor, "name")
	return typeof rawName === "string" ? rawName : "unnamed"
}

function isFaceParserLogits(tensor: tf.Tensor) {
	return (
		tensor.shape.length === 4 &&
		tensor.shape[1] === UNIFACE_FACE_PARSER_INPUT_SIZE &&
		tensor.shape[2] === UNIFACE_FACE_PARSER_INPUT_SIZE &&
		tensor.shape[3] === 19
	)
}
